import { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { motion } from 'framer-motion';
import { Search, Mail, Phone, MapPin, Briefcase, Network, X, Calendar } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';

const API = '/api';

function getInitials(name) {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
}

const avatarColors = [
  'bg-[#16A34A] text-white',
  'bg-[#F59E0B] text-white',
  'bg-[#3B82F6] text-white',
  'bg-[#8B5CF6] text-white',
  'bg-[#EC4899] text-white',
  'bg-[#14B8A6] text-white',
  'bg-[#F97316] text-white',
  'bg-[#6366F1] text-white',
];

function getAvatarColor(name) {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return avatarColors[Math.abs(hash) % avatarColors.length];
}

function EmployeeCard({ user, index }) {
  const initials = getInitials(user.name);
  const colorClass = getAvatarColor(user.name);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.04 }}
    >
      <Card className="bg-white border-[#E2E8F0] hover:border-[#16A34A]/40 hover:shadow-md transition-all" data-testid={`employee-card-${user.id}`}>
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <div className={`w-14 h-14 rounded-full ${colorClass} flex items-center justify-center text-xl font-bold flex-shrink-0`} data-testid={`avatar-${user.id}`}>
              {initials}
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="font-semibold text-[#052E16] text-lg font-['Manrope'] truncate">
                {user.name}
              </h3>
              {user.position && (
                <p className="text-sm text-[#16A34A] font-medium mt-0.5">{user.position}</p>
              )}
              {user.department && (
                <div className="flex items-center gap-1.5 text-sm text-[#57534E] mt-2">
                  <Briefcase className="w-3.5 h-3.5 flex-shrink-0" />
                  <span className="truncate">{user.department}</span>
                </div>
              )}
              <div className="flex items-center gap-1.5 text-sm text-[#57534E] mt-1">
                <Mail className="w-3.5 h-3.5 flex-shrink-0" />
                <a href={`mailto:${user.email}`} className="hover:text-[#16A34A] transition-colors truncate">{user.email}</a>
              </div>
              {user.phone && (
                <div className="flex items-center gap-1.5 text-sm text-[#57534E] mt-1">
                  <Phone className="w-3.5 h-3.5 flex-shrink-0" />
                  <a href={`tel:${user.phone}`} className="hover:text-[#16A34A] transition-colors">{user.phone}</a>
                </div>
              )}
              {user.office && (
                <div className="flex items-center gap-1.5 text-sm text-[#57534E] mt-1">
                  <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
                  <span>{user.office}</span>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}

export default function DirectoryPage() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [orgInfo, setOrgInfo] = useState(null);
  const [showOrg, setShowOrg] = useState(false);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const { data } = await axios.get(`${API}/directory`, { withCredentials: true });
        setUsers(data);
      } catch (error) {
        console.error('Error fetching directory:', error);
      } finally {
        setLoading(false);
      }
    };
    const fetchOrg = async () => {
      try {
        const { data } = await axios.get(`${API}/organigram/info`, { withCredentials: true });
        setOrgInfo(data);
      } catch (error) {
        console.error('Error fetching organigram info:', error);
      }
    };
    fetchUsers();
    fetchOrg();
  }, []);

  const filtered = users.filter(u => {
    const q = search.toLowerCase();
    return u.name.toLowerCase().includes(q)
      || u.email.toLowerCase().includes(q)
      || (u.department || '').toLowerCase().includes(q)
      || (u.position || '').toLowerCase().includes(q);
  });

  return (
    <Layout>
      <div className="space-y-6" data-testid="directory-page">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-3xl lg:text-4xl font-bold text-[#052E16] font-['Manrope']">
              Adresář
            </h1>
            <p className="text-[#57534E] mt-1">
              Kontakty zaměstnanců
            </p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            {orgInfo?.exists && (
              <Button
                onClick={() => setShowOrg(true)}
                data-testid="open-organigram-btn"
                className="bg-[#16A34A] hover:bg-[#15803D] text-white font-semibold px-5 py-2.5 rounded-xl shadow-sm text-sm gap-2"
              >
                <Network className="w-4 h-4" />
                Organizační struktura
              </Button>
            )}
            <div className="relative w-full sm:w-72">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
              <Input
                placeholder="Hledat podle jména, oddělení..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                data-testid="directory-search-input"
                className="pl-10 bg-white border-[#E2E8F0] text-[#1C1917] focus:border-[#16A34A]"
              />
            </div>
          </div>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="h-36 rounded-2xl bg-[#F0FDF4] animate-pulse" />
            ))}
          </div>
        ) : filtered.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="directory-grid">
            {filtered.map((user, index) => (
              <EmployeeCard key={user.id} user={user} index={index} />
            ))}
          </div>
        ) : (
          <Card className="bg-white border-[#E2E8F0]">
            <CardContent className="py-12 text-center">
              <p className="text-[#57534E]">
                {search ? 'Žádné výsledky pro vaše hledání' : 'Zatím nejsou žádní zaměstnanci'}
              </p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Organigram Full-Screen Viewer */}
      {showOrg && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex flex-col" data-testid="organigram-viewer">
          {/* Header bar */}
          <div className="flex items-center justify-between px-6 py-3 bg-white border-b border-[#E2E8F0] shadow-sm">
            <div className="flex items-center gap-3">
              <Network className="w-5 h-5 text-[#16A34A]" />
              <h2 className="text-lg font-semibold text-[#052E16] font-['Manrope']">
                Organizační struktura
              </h2>
              {orgInfo?.uploaded_at && (
                <span className="flex items-center gap-1.5 text-xs text-[#57534E] bg-[#F0FDF4] px-2.5 py-1 rounded-full border border-[#E2E8F0]">
                  <Calendar className="w-3 h-3" />
                  Platnost od: {new Date(orgInfo.uploaded_at).toLocaleDateString('cs-CZ')}
                </span>
              )}
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowOrg(false)}
              data-testid="close-organigram-btn"
              className="text-[#57534E] hover:text-[#052E16] hover:bg-[#F0FDF4]"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>
          {/* PDF embed */}
          <div className="flex-1 bg-[#F1F5F9]">
            <iframe
              src={`${API}/organigram/file`}
              title="Organizační struktura"
              className="w-full h-full border-0"
              data-testid="organigram-iframe"
            />
          </div>
        </div>
      )}
    </Layout>
  );
}
